import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { MultiSelectModule } from 'primeng/multiselect';
import { FilterService } from 'primeng/api';

export interface ExcelFilterOption {
  label: string;
  value: string | boolean | number;
}

/**
 * Sentinel values representing "(Empty)" and "(Not Empty)" pseudo-entries in the checklist filter,
 * so they can sit at the top of the checkbox list alongside the real values - checking "(Empty)"
 * filters for blank (null / undefined / '') cells, checking "(Not Empty)" filters for any cell that
 * has a value, same as Excel's AutoFilter "Blanks" concept but under clearer, explicit labels.
 */
export const EXCEL_FILTER_EMPTY_VALUE = '\u0000__empty__';
export const EXCEL_FILTER_NOT_EMPTY_VALUE = '\u0000__not_empty__';

let matchModesRegistered = false;

/** Registers the custom PrimeNG FilterService match modes used by this component (idempotent). */
function registerExcelFilterMatchModes(filterService: FilterService): void {
  if (matchModesRegistered) {
    return;
  }
  matchModesRegistered = true;

  // Checklist filter: same semantics as PrimeNG's built-in 'in', except:
  //  - a blank row value only matches if EXCEL_FILTER_EMPTY_VALUE is among the selected values
  //  - a non-blank row value also matches if EXCEL_FILTER_NOT_EMPTY_VALUE is selected (in addition
  //    to matching one of the real checked values)
  filterService.register('inOrBlank', (value: unknown, filterValues: unknown[] | null | undefined): boolean => {
    if (!Array.isArray(filterValues) || filterValues.length === 0) {
      return true;
    }
    const isBlank = value === null || value === undefined || value === '';
    if (isBlank) {
      return filterValues.includes(EXCEL_FILTER_EMPTY_VALUE);
    }
    if (filterValues.includes(EXCEL_FILTER_NOT_EMPTY_VALUE)) {
      return true;
    }
    return filterValues.some((f) => f === value || String(f) === String(value));
  });

  // Text filter extras: "Is empty" / "Is not empty", offered alongside the standard contains/
  // startsWith/etc. match modes via [matchModeOptions].
  filterService.register('isEmpty', (value: unknown): boolean => value === null || value === undefined || value === '');
  filterService.register('isNotEmpty', (value: unknown): boolean => !(value === null || value === undefined || value === ''));
}

/**
 * Excel-style column filter for a PrimeNG p-table, matching the filter UX used by the "openPunches"
 * table on the dashboard (app-user-punch / ColumnFilterDirective): a filter icon on the column header
 * opens a dropdown with a searchable, multi-select checkbox list of the column's unique values -
 * with "(Empty)" / "(Not Empty)" entries pinned to the top so empty cells can be filtered for/out.
 *
 * For free-text columns (too many unique values to usefully check off, e.g. Description), set
 * `type="text"` to fall back to a simple text filter, whose match-mode dropdown also offers
 * "Is empty" / "Is not empty" alongside the usual contains/starts with/etc.
 *
 * Usage:
 * ```html
 * <app-excel-column-filter field="tagNumber" [options]="columnFilterOptions['tagNumber']"></app-excel-column-filter>
 * <app-excel-column-filter field="description" type="text"></app-excel-column-filter>
 * ```
 */
@Component({
  selector: 'app-excel-column-filter',
  standalone: true,
  imports: [CommonModule, FormsModule, TableModule, MultiSelectModule],
  templateUrl: './excel-column-filter.component.html',
})
export class ExcelColumnFilterComponent {
  /** The p-table field/property name to filter on. */
  @Input({ required: true }) field!: string;

  /** 'checklist' (default) shows a multi-select checkbox dropdown, 'text' shows a simple text filter. */
  @Input() type: 'checklist' | 'text' = 'checklist';

  /** Unique value options for the checklist filter ("(Empty)" / "(Not Empty)" are added on top automatically). */
  @Input() options: ExcelFilterOption[] = [];

  /** Match mode used for the checklist filter (PrimeNG FilterService); supports empty/not-empty by default. */
  @Input() matchMode = 'inOrBlank';

  @Input() placeholder = 'Any';
  @Input() minWidth = '200px';
  @Input() showSearch = true;

  /** Standard text match modes plus custom "Is empty" / "Is not empty". */
  readonly textMatchModeOptions = [
    { label: 'Contains', value: 'contains' },
    { label: 'Does not contain', value: 'notContains' },
    { label: 'Starts with', value: 'startsWith' },
    { label: 'Ends with', value: 'endsWith' },
    { label: 'Equals', value: 'equals' },
    { label: 'Not equals', value: 'notEquals' },
    { label: 'Is empty', value: 'isEmpty' },
    { label: 'Is not empty', value: 'isNotEmpty' },
  ];

  constructor(private readonly filterService: FilterService) {
    registerExcelFilterMatchModes(this.filterService);
  }

  /** "(Empty)" / "(Not Empty)" pinned on top, followed by the real unique-value options. */
  get checklistOptions(): ExcelFilterOption[] {
    return [
      { label: '(Empty)', value: EXCEL_FILTER_EMPTY_VALUE },
      { label: '(Not Empty)', value: EXCEL_FILTER_NOT_EMPTY_VALUE },
      ...this.options,
    ];
  }
}
